// Debugging
export const IS_DEBUG = process.env.NODE_ENV === 'development';

// External resources
export const LOGO_URL = 'https://timmons-branding.s3.amazonaws.com/South-Dakota/ARPHUB/logoWithText.png';
export const ICON_URL = 'https://timmons-branding.s3.amazonaws.com/South-Dakota/ARPHUB/bigIcon.png';
export const SPLASH_URL = 'https://timmons-branding.s3.amazonaws.com/South-Dakota/ARPHUB/splashBackground.png';

// Authentication states
export const AUTH_STATES = Object.freeze({
  INITIALIZING: 'INITIALIZING',
  LOGGED_OUT: 'LOGGED_OUT',
  LOGGING_IN: 'LOGGING_IN',
  LOGGED_IN: 'LOGGED_IN',
  TOKEN_STALE: 'TOKEN_STALE',
  REFRESHING_TOKEN: 'REFRESHING_TOKEN',
  ERROR: 'ERROR'
});

// Data loading states
export const DATA_STATES = Object.freeze({
  IDLE: 0,
  LOADING: 1,
  LOADED: 2,
  ERROR: 3
});

// Access Control List (ACL) values
export const ACLS = Object.freeze({
  ANON: 'Anonymous',
  SIGN_IN: 'Can Sign In',
  SEE_HOME: 'Can See Home Page',
  SEE_RECIP: 'Can See Recipients Page',
  CREATE_RECIP: 'Can Create Recipients',
  EDIT_RECIP: 'Can Update Recipients',
  EDIT_ORG_RECIP: 'Can Update Recipients',
  CREATE_ORG_RECIP: 'Can Create Org Recipients',
  SEE_ORG_RECIP: 'Can See Org Recipients Page',
  SEE_SEARCH_PAGE: 'Can See Search Page',
  SEE_UNITS: 'Can See Units Page',
  SEE_ACTIVITIES: 'Can See Activities Page',
  SEE_PRACTICES: 'Can See Practice Details Page',
  DEVELOPER: 'DEVELOPER'
});

// Field type values
export const FIELD_TYPES = Object.freeze({
  TEXT: 0,
  LONG_TEXT: 1,
  INT: 2,
  FLOAT: 3,
  CURRENCY: 4,
  DATE: 5,
  FLAG: 6,
  CHOICE: 7,
  OBJECT: 10,
  // BYNAMEOBJECTREFERENCE: 11,
  SPATIAL: 20,
  LINK: 100,
  CLUSTER: 120,
  NONE: 999
});

export const REQUIRED = 'required';

export const RENDER_VALIDATIONS = Object.freeze({
  REQUIRED,
  DISABLED: 'disabled',
  HIDDEN: 'hidden',
});

export const VALIDATIONS = Object.freeze({
  REQUIRED,
  INTEGER_DIGITS: 'integerDigits',
  FRACTIONAL_DIGITS: 'fractionalDigits',
  MAX_VALUE: 'maxValue',
  MAX_LENGTH: 'maxLength',
  MIN_LENGTH: 'minLength',
  EMAIL: 'email',
  PHONE: 'phone',
  ZIP: 'zip',
});

// Regex
export const REGEX = Object.freeze({
  PHONE: /^$|^\d{3}-\d{3}-\d{4}$/,
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  ZIP: /^$|^\d{5}(-\d{4})?$/,
});

// Local Storage
export const LOCAL_STORAGE_KEYS = Object.freeze({
  RECIPIENT_TABBER: 'recipient:tabber',
});

// Activity Layer Fields names (from AGS service) and UI Label correspondence
// (only needed for those fields in the activity layer that are abbreviated in a way that
// the automatic conversion the backend does (underscores to blank spaces, and capitalize every word)
// does not result in the label that is desired to be shown in the UI). See getActivityFieldLabel.
// If any field names are changed in the GIS data model,
// they must also be updated here.
export const ACTIVITY_FIELD_LABEL_LOOKUP = Object.freeze({
  'tsi_type': 'TSI Type',
  'commercial_tsi_type': 'Commercial TSI Type',
  'non_commercial_tsi_type': 'Non-Commercial TSI Type',
  'cf_assistance_type': 'Assistance Type',
  'total_planting_length': 'Total Planting Length (ft)',
  'forest_management_plan': 'Included in Forest Management Plan?',
  'prescription_completed': 'Prescription Completed?',
  'management_plan': 'Recommended in Management Plan?',
  'trees_planted': 'Number of Trees Planted',
  'acres_completed': 'Number of Acres Completed',
  'acres_harvested': 'Number of Acres Harvested',
  'sawtimber_mbf': 'Sawtimber MBF',
  'rcf_program': 'RCF Program',
  'plan_prepared_reported': 'Prescription prepared (reported separately)',
  'fhs_assistance_request': 'Forest Health Specialist Assistance Request',
  'num_of_display_visits': 'Number of Display Visits',
  'i_and_e_subject': 'I&E Subject',
  'num_of_attendees': 'Enter Number of Attendees',
  'num_of_participants': 'Enter Number of Participants',
  'num_of_volunteers': 'Enter Number of Volunteers',
  'green_stormwater_infra': 'Green Stormwater Infrastructure?',
  'comm_prog_level': 'Community Program Level',
  'form_prog_level': 'Formative Program Level',
  'dev_prog_level': 'Developmental Program Level',
  'dev_level_assist': 'Developmental Program Level',
  'sus_prog_level': 'Sustained Program Level',
  'grant_num_bill_code': 'Grant Number/Billing Code',
  'stabil_function': 'Stabilization Function',
  'stabil_pract': 'Stabilization Practice',
  'num_wb_improved': 'Number WB Improved',
  'rehab_function': 'Rehabilitation Function',
  'rehab_practice': 'Rehabilitation Practice',
  'num_of_trees': 'Number of Trees',
  'well_seal_funct': 'Well Sealing Function',
  'igp_assist_type': 'IGP Assistance Type',
  'icd_assist_type': 'ICD Assistance Type',
  'icp_assist_type': 'ICP Assistance Type',
  'rm_assist_type': 'RM Assistance Type',
  'cg_assistance_type': 'Assistance Type',
  'cost_share_program_text': 'Cost Share Program',
  'plan_categories': 'Plan Category'
});

// Lookup to get the list of activity specific fields by Activity Name
export const ACTIVITY_SPECIFIC_FIELDS = Object.freeze({
  'Timber Stand Improvement Assist': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program', 'reported_acres', 'plan_prepared_reported'],
  'Tree Planting Assist': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program', 'reported_acres', 'plan_prepared_reported'],
  'Individual Tree Question': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program', 'reported_acres'],
  'Windbreak Assist': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program', 'plan_prepared_reported'],
  'Renovation Assist': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program', 'plan_prepared_reported'],
  'Species Recommendation': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program'],
  'Species Identification': ['stewardship_programs', 'good_neighbor_authority', 'rcf_program'],

  'Forest Stewardship Plan': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'plan_acres', 'forest_stewardship_plan_type',
    'plan_start_date', 'plan_duration_years', 'potential_commercial_acres', 'management_objectives', 'funding_source'],

  'Tree Planting Plan': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'plan_acres', 'plan_start_date', 'plan_duration_years',
    'primary_planting_function', 'total_planting_length', 'new_establishment_type', 'restoration_type'],

  'Other Management Plan': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'plan_acres', 'plan_categories', 'plan_start_date',
    'plan_duration_years', 'management_objectives', 'practice_project_type', 'plan_type', 'funding_source'],

  'TSI Prescription': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'prescription_acres', 'tsi_type', 'cost_share_programs',
    'management_objectives', 'forest_management_plan', 'commercial_tsi_type', 'non_commercial_tsi_type'],

  'Wood Harvest Prescription': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'prescription_acres', 'prescription_completed',
    'acres_completed', 'sawtimber_mbf', 'roudwood_cords', 'tons_biomass'],

  'Regeneration Prescription': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'prescription_acres', 'regeneration_practice',
    'regeneration_activity', 'cost_share_programs', 'prescription_completed', 'acres_completed', 'trees_planted'],

  'Tree Planting': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'cost_share_programs', 'management_plan', 'trees_planted'],

  'Non-Rx Wood Harvest': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'acres_harvested', 'sawtimber_mbf', 'roudwood_cords', 'tons_biomass'],

  'Spot Checks': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'spot_check_program'],

  'Other Practices': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'practice_acres', 'cost_share_programs', 'practice_project_type'],

  'Insect and Disease Assist': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'assist_type', 'host', 'symptom_location',
    'symptom_description', 'cause', 'cause_description', 'fhs_assistance_request'],

  'Presentation': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'i_and_e_subject', 'event_name', 'num_of_attendees', 'other_subject'],
  'Workshop': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'i_and_e_subject', 'other_subject', 'contact_hours', 'num_of_participants'],
  'Media Relations': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'media_type', 'i_and_e_subject', 'other_subject'],
  'Static Display': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'display_type', 'num_of_display_visits', 'i_and_e_subject', 'other_subject'],

  'UCF Project Assist': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'cf_assistance_type', 'num_of_volunteers', 'dev_prog_level',
    'description', 'big_tree_status', 'funding_source'],

  'UCF Community Assist': ['person_hours', 'stewardship_programs', 'good_neighbor_authority', 'num_of_volunteers', 'green_stormwater_infra', 'comm_prog_level',
    'form_prog_level', 'dev_level_assist', 'sus_prog_level', 'funding_source'],

  'Fuels Reduction': ['stewardship_programs', 'good_neighbor_authority', 'treatment_type', 'contract_acres', 'cost_share', 'grant_num_bill_code', 'cost_amount'],
  'Grass Seeding': ['cg_assistance_type', 'acres', 'seeding_function', 'resource_improved', 'cost_share_program_text', 'acres_improved', 'num_wb_improved'],
  'Wildfire Rehabilitation': ['cg_assistance_type', 'acres', 'rehab_function', 'rehab_practice', 'num_of_trees', 'cost_share_program_text'],
  'Earthwork': ['cg_assistance_type', 'earthwork_function', 'resource_improved', 'quantity', 'cost_share_program_text', 'acres_improved', 'number_improved'],
  'Grade/Shoreline Stabilization': ['cg_assistance_type', 'stabil_function', 'stabil_pract', 'num_wb_improved', 'quantity', 'cost_share_program_text'],
  'Abandoned Well Sealing': ['cg_assistance_type', 'well_seal_funct', 'quantity'],
  'Incidental Grant Program Assists': ['stewardship_programs', 'igp_assist_type', 'quantity'],
  'Incidental Conservation District Assists': ['stewardship_programs', 'icd_assist_type', 'quantity'],
  'Incidental Conservation Program Assists': ['stewardship_programs', 'icp_assist_type', 'quantity'],
  'Range Management': ['rm_assist_type', 'quantity']
});
